// ── Синхронизация: состояние для шапки и кнопка «Синхронизировать сейчас» ────
// useSyncStatus — «Синхронизация…» и её итог: result 'ok' | 'error' | 'no_tables',
// detail — причина ошибки (подсказка). Итог виден несколько секунд и сам уходит;
// новая синхронизация отменяет таймер прежней (раньше старый таймер мог стереть
// свежий итог раньше времени).
// useManualSync — полная синхронизация по кнопке, жесту «потянуть вниз» и сама:
// при возврате в приложение (если с прошлой > 2 мин) и раз в 5 минут.
// Правки уходят на сервер сами, сразу после изменения (outbox.js) — здесь
// забираем сделанное на других устройствах и досылаем то, что не ушло.

import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '../i18n';
import { flushOutbox, outboxSize } from '../outbox';
import { flushCustomLangsQueue, pullCustomLangsRemote, syncDailyStats, syncWithSupabase } from '../storage';
import { pullTextProjects } from '../textProjects';

const LAST_SYNC = 'ac_last_sync';
const sinceLastSync = () => { try { return Date.now() - parseInt(localStorage.getItem(LAST_SYNC) || '0', 10); } catch { return Infinity; } };
export const markSynced = () => { try { localStorage.setItem(LAST_SYNC, String(Date.now())); } catch { /* приватный режим */ } };

export function useSyncStatus() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [detail, setDetail] = useState('');
  const timer = useRef(null);
  const start = useCallback(() => { clearTimeout(timer.current); setResult(null); setDetail(''); setBusy(true); }, []);
  const finish = useCallback((res, why = '', ms = 6000) => {
    setBusy(false); setResult(res); setDetail(why);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { setResult(null); setDetail(''); }, ms);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return { busy, result, detail, start, finish };
}

// onProjects(list, uid) — пришли аудиопроекты; onTexts(list) — пришли тексты.
export function useManualSync(session, status, { onProjects, onTexts }) {
  const uid = session?.user?.id;
  const cbs = useRef({ onProjects, onTexts });
  cbs.current = { onProjects, onTexts };
  const { busy, start, finish } = status;

  const sync = useCallback(async () => {
    if (!uid || busy) return;
    start();
    try {
      // 1. Аудиопроекты ← сервер (главный шаг: от него зависит итог).
      const r = await syncWithSupabase(uid);
      if (r.success && r.projects) cbs.current.onProjects?.(r.projects, uid);
      // 2. Тексты ← сервер. Свои правки уже ушли очередью в шаге 1.
      let textsOk = true;
      try { cbs.current.onTexts?.(await pullTextProjects(uid)); } catch (e) { textsOk = false; console.warn('[Sync] text pull:', e?.message); }
      // 3. Словарь: правки слов тоже идут очередью — просим перечитать.
      window.dispatchEvent(new CustomEvent('ac_reload_vocab'));
      // 4. Статистика по дням (слушание, чтение, карточки).
      try { await syncDailyStats(uid); } catch (e) { console.warn('[Sync] daily stats:', e); }
      // 5. Свои языки: дослать отложенное, забрать список.
      try { await flushCustomLangsQueue(uid); await pullCustomLangsRemote(uid); } catch (e) { console.warn('[Sync] custom langs:', e); }
      // Ещё раз дошлём очередь: «Синхронизировано» — только если она пуста.
      const pushErr = (await flushOutbox().catch((e) => ({ error: e?.message }))).error;
      if (r.success) markSynced();
      if (!r.success) finish('error', t("шаг 1 (audio): {0}", [r.reason || 'unknown']));
      else if (outboxSize() > 0) finish('error', t("не отправлено изменений: {0}{1} — повторю сам", [outboxSize(), pushErr ? ` (${pushErr})` : '']));
      else if (!textsOk) finish('no_tables'); // аудио есть, а таблицы текстов нет
      else finish('ok');
    } catch (e) {
      console.error('[ManualSync] caught', e);
      finish('error', `caught: ${e?.message || e?.name || 'unknown'}`, 8000);
    }
  }, [uid, busy, start, finish]);

  // Сама: при возврате в приложение и раз в 5 минут.
  const syncRef = useRef(sync);
  syncRef.current = sync;
  useEffect(() => {
    if (!uid) return undefined;
    const onBack = () => { if (document.visibilityState === 'visible' && sinceLastSync() > 2 * 60 * 1000) syncRef.current?.(); };
    const id = setInterval(() => { if (document.visibilityState === 'visible' && sinceLastSync() > 5 * 60 * 1000) syncRef.current?.(); }, 60 * 1000);
    document.addEventListener('visibilitychange', onBack);
    window.addEventListener('online', onBack);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onBack); window.removeEventListener('online', onBack); };
  }, [uid]);

  return sync;
}
